import { useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useTranslation } from '../i18n/useTranslation.js';
import { financeApi } from '../services/financeApi.js';
import { inventoryApi } from '../services/inventoryApi.js';
import { salesApi } from '../services/salesApi.js';

/**
 * "Heute wichtig": one strip that surfaces every queue waiting for a decision
 * (credit holds, fulfillable backorders, approvals, overdue AR, due recurring
 * journals) and deep-links straight to the page where it is handled. Renders
 * nothing when there is nothing to do.
 */
export function ActionCenter() {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);

  const [holds, backorders, pos, reqs, overdue, recurring] = useQueries({
    queries: [
      { queryKey: ['action', 'credit-holds'], queryFn: () => salesApi.creditHolds().then((r) => r.items), retry: false },
      { queryKey: ['action', 'backorders'], queryFn: () => inventoryApi.listBackorders({ status: 'OPEN' }).then((r) => r.items), retry: false },
      { queryKey: ['action', 'po-approvals'], queryFn: () => inventoryApi.listPurchaseOrders({ status: 'PENDING_APPROVAL' }).then((r) => r.items), retry: false },
      { queryKey: ['action', 'banf'], queryFn: () => inventoryApi.listRequisitions({ status: 'SUBMITTED' }).then((r) => r.items), retry: false },
      { queryKey: ['action', 'overdue'], queryFn: () => financeApi.dunningOverdue().then((r) => r.items?.invoices || []), retry: false },
      { queryKey: ['action', 'recurring-due'], queryFn: () => financeApi.listRecurringJournals().then((r) => r.items), retry: false },
    ],
  });

  const backorderRows = backorders.data || [];
  const readyBackorders = backorderRows.filter((b) => Number(b.available) >= Number(b.quantity)).length;
  const dueRecurring = (recurring.data || []).filter((r) => r.active && r.nextRun && r.nextRun <= today).length;

  const items = [
    { key: 'creditHolds', count: (holds.data || []).length, to: '/sales/credit', label: t('dashboard.actions.creditHolds') },
    { key: 'backorders', count: readyBackorders, to: '/inventory/backorders', label: t('dashboard.actions.backordersReady') },
    { key: 'poApprovals', count: (pos.data || []).length, to: '/inventory/purchase-orders', label: t('dashboard.actions.poApprovals') },
    { key: 'banf', count: (reqs.data || []).length, to: '/inventory/requisitions', label: t('dashboard.actions.banfPending') },
    { key: 'overdue', count: (overdue.data || []).length, to: '/finance/dunning', label: t('dashboard.actions.overdueAr') },
    { key: 'recurring', count: dueRecurring, to: '/finance/recurring-journals', label: t('dashboard.actions.recurringDue') },
  ].filter((i) => i.count > 0);

  if (items.length === 0) return null;

  return (
    <div className="action-center">
      <span className="action-center-title">{t('dashboard.actions.title')}</span>
      <div className="action-center-items">
        {items.map((item) => (
          <Link key={item.key} to={item.to} className="action-pill">
            <span className="action-pill-count">{item.count}</span>
            <span>{item.label}</span>
            <span aria-hidden="true" className="action-pill-arrow">→</span>
          </Link>
        ))}
        <Link to="/inbox" className="action-center-inbox">{t('dashboard.actions.toInbox')}</Link>
      </div>
    </div>
  );
}
